import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { MemberOrganization } from "@/models/organization.model";
import { OrganizationRepository } from "@/repositories/organization.repository";
import { uuid } from "@/interfaces";

interface OrganizationState {
    organizations: MemberOrganization[];
    activeOrgId: uuid | null;
    loading: boolean;
}

const initialState: OrganizationState = {
    organizations: [],
    activeOrgId: null,
    loading: true,
};

export const fetchOrganizations = createAsyncThunk(
    "organization/fetchOrganizations",
    async () => {
        const orgs = await OrganizationRepository.getUserOrganizations();

        const localActiveOrg = localStorage.getItem("active_org_id");
        const valid = orgs.find((o) => o.id === localActiveOrg);
        const active = valid?.id ?? orgs[0]?.id ?? null;

        if (active) {
            localStorage.setItem("active_org_id", active);
        }

        return { orgs, activeOrgId: active };
    }
);

const organizationSlice = createSlice({
    name: "organization",
    initialState,
    reducers: {
        setActiveOrgId: (state, action: PayloadAction<uuid>) => {
            state.activeOrgId = action.payload;
            localStorage.setItem("active_org_id", action.payload);
        },
        organizationShown: (state, action: PayloadAction<{ id: uuid; name: string; logo: string | null }>) => {
            const shown = state.organizations.find((organization) => organization.id === action.payload.id);
            if (!shown) return;
            shown.display_name = action.payload.name;
            shown.image = action.payload.logo;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchOrganizations.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchOrganizations.fulfilled, (state, action) => {
                state.organizations = action.payload.orgs;
                state.activeOrgId = action.payload.activeOrgId;
                state.loading = false;
            })
            .addCase(fetchOrganizations.rejected, (state) => {
                state.loading = false;
            });
    },
});

export const { setActiveOrgId, organizationShown } = organizationSlice.actions;
export default organizationSlice.reducer;